/* global CKEDITOR, CKEDITOR_TAGS_NAME */

import * as Toast from "./components/toast.js";

let initiated = false;

export const CONTENT_EDITOR_NAME = "custom_document_template_html";

// A page can hold two tag lists at once: the drag panel of a template editor, and the one in the
// email popup that the app layout renders everywhere. They need distinct ids, otherwise the list
// each piece of code reaches depends on which one the markup happens to render first.
export const TAGS_LIST_ID = "tags-list";
export const EMAIL_TAGS_LIST_ID = "email-tags-list";

const TOOLBAR = [
  { name: "clipboard", items: ["Cut", "Copy", "Paste", "PasteText", "PasteFromWord", "-", "Undo", "Redo"] },
  {
    name: "basicstyles",
    items: ["Bold", "Italic", "Underline", "Strike", "Subscript", "Superscript", "-", "CopyFormatting", "RemoveFormat"],
  },
  {
    name: "paragraph",
    items: [
      "NumberedList",
      "BulletedList",
      "-",
      "Outdent",
      "Indent",
      "-",
      "JustifyLeft",
      "JustifyCenter",
      "JustifyRight",
      "JustifyBlock",
    ],
  },
  { name: "links", items: ["Link", "Unlink"] },
  /* We modified the plugin to restrict the upload of .bmp files and file size exceeds the 200k.*/
  { name: "insert", items: ["base64image", "Table", "PageBreak"] },
  { name: "styles", items: ["Format", "Font", "FontSize"] },
  { name: "colors", items: ["TextColor", "BGColor"] },
  { name: "tools", items: ["Maximize"] },
];

let errorDialogTimer;
let accumulatedErrors = {};

function updateTagErrorDialog() {
  if (errorDialogTimer) {
    clearTimeout(errorDialogTimer);
  }

  errorDialogTimer = setTimeout(() => {
    // We need to copy the map so AlpineJS notices the changes
    const event = new CustomEvent("digiforma-errored-tags", { detail: { errors: { ...accumulatedErrors } } });
    window.dispatchEvent(event);
    errorDialogTimer = null;
  }, 20);
}

function addErroredTag(tag) {
  accumulatedErrors[tag] = accumulatedErrors[tag] ? accumulatedErrors[tag] + 1 : 1;
  updateTagErrorDialog();
}

function removeErroredTag(tag) {
  accumulatedErrors[tag] = accumulatedErrors[tag] ? accumulatedErrors[tag] - 1 : 0;
  if (!accumulatedErrors[tag]) {
    delete accumulatedErrors[tag];
  }
  updateTagErrorDialog();
}

export async function initCKEditor(textareaId, opts = {}) {
  const textareaEl = document.getElementById(textareaId);

  // Set height of the textarea to the height in parameter
  let temporaryHeight = opts.height;
  if (!opts.oneLine) {
    // Add top and bottom toolbar height
    temporaryHeight += 42 + 28;
  }
  textareaEl.style.height = temporaryHeight + "px";

  return import("ckeditor4")
    .then(() => {
      textareaEl.innerHTML = putTagsLabel(textareaEl.innerHTML);

      // Remove the disabled attribute on the textarea if it has the
      // data-disabled-while-loading attribute
      // The disalbed attribute was there to prevent the user from editing the
      // content while CKEditor was loading.
      if (textareaEl.dataset.disabledWhileLoading !== undefined) {
        textareaEl.disabled = false;
      }

      beforeInitCKEditor();

      const basePlugins = ["tag", "pastetools"];
      const fullpagePlugins = [
        "base64image",
        "colorbutton",
        "colordialog",
        "copyformatting",
        "font",
        "format",
        "ifntag",
        "iftag",
        "indentblock",
        "justify",
        "imageresize",
        "pagebreak",
        "tableresize",
        "thirds",
      ];
      const extraPlugins = opts.oneLine ? basePlugins.concat("oneLine") : basePlugins.concat(fullpagePlugins);

      CKEDITOR.replace(textareaId, {
        language: window._locale,
        customConfig: "",
        extraPlugins: extraPlugins.join(","),
        toolbar: opts.toolbar || TOOLBAR,
        contentsCss: opts.contentsCss,
        imageResize: { maxWidth: 600, maxHeight: 600 },
        height: opts.height,
        removePlugins: "ckeditortablecellsselection,elementspath",
        font_names:
          "Calibri/Calibri, sans-serif; Arial/Arial; Courier/Courier, Courier New, monospace; Georgia/Georgia, serif; Helvetica, sans-serif; Monaco/Monaco, Lucida Console, monospace; Times New Roman/Times New Roman, Times, serif; Verdana/Verdana; Comic Sans MS/Comic Sans MS, cursive;",
        fontSize_sizes:
          "6/6pt;7/7pt;8/8pt;9/9pt;10/10pt;11/11pt;12/12pt;13/13pt;14/14pt;16/16pt;18/18pt;20/20pt;22/22pt;24/24pt;26/26pt;28/28pt;30/30pt;34/34pt;38/38pt;42/42pt;52/52pt",
      });

      $(".editor-column").css("visibility", "visible");
      return Promise.resolve();
    })
    .catch((error) => console.error(`ckeditor4 loading failed : ${error}`));
}

function beforeInitCKEditor() {
  const input_filter_tag = document.getElementById("filter_tags");

  if (input_filter_tag) {
    input_filter_tag.addEventListener("keyup", _.debounce(onFilter, 200));
  }
  initTagsList();
  if (!initiated) {
    initiated = true;
    createTagPlugin();
    createIfTagPlugin("iftag", CKEDITOR_TAGS_NAME.if);
    createIfTagPlugin("ifntag", CKEDITOR_TAGS_NAME.unless);
    createThirdsPlugin();
    createOneLinePlugin();
  }
}

export function initTagsList(filter = "", listId = TAGS_LIST_ID) {
  replacementTagToNameLookup = void 0;
  const targetTagsList = $(`#${listId}`);

  if (targetTagsList.data("jsNoTagInit") !== undefined) {
    return;
  }

  const thirdsHtml = `<li><div id="thirds" class="badge badge--xs has-cursor-grab has-bg-blue-100 has-text-white has-mt-02" draggable="true">${CKEDITOR_TAGS_NAME.thirds}</div></li>`;

  let html = "";
  let index = 0;
  let currentType = "";
  for (var tag of window.replacements_tags) {
    if (tag.type != currentType) {
      html += `<li><span class="label label--sm">${CKEDITOR_TAGS_NAME[tag.type] || tag.type}</span></li>`;
      currentType = tag.type;

      if (currentType === "caces_certificate") {
        html += thirdsHtml;
      }
    }
    if (tag.name.toUpperCase().includes(filter.toUpperCase()) && !tag.name.includes("hidden")) {
      html += `<li><div class="badge badge--xs has-cursor-grab has-bg-purple-100 has-text-white has-mt-02" draggable="true" data-tag-index="${index}">${tag.name}</div></li>`;
    }
    index++;
  }
  html += `<p>${CKEDITOR_TAGS_NAME.procedures}</p>`;
  html += `<li></li><div id="iftag" class="badge badge--xs has-cursor-grab has-bg-blue-100 has-text-white" draggable="true">${CKEDITOR_TAGS_NAME.if_then}</div></li>`;
  html += `<li></li><div id="ifntag" class="badge badge--xs has-cursor-grab has-bg-blue-100 has-text-white has-mt-02" draggable="true">${CKEDITOR_TAGS_NAME.else_if_then}</div></li>`;
  targetTagsList.html(html);
}

function onFilter(e) {
  initTagsList(e.target.value);
}

/**
 * Update a string to add labels inside tags HTML.
 *
 * e.g. `<p><span data-tag="user_company"></span></p>` => `<p><span data-tag="user_company">Nom de l'organisme</span></p>`
 *
 * @param {String} text The original content of the document
 * @returns {String} The updated content of the document
 */
function putTagsLabel(text) {
  const parser = new DOMParser();
  // At this point, the text is HTML encoded (`<` => `&lt;`), so we must first decode it
  const unescaped = parser.parseFromString(text, "text/html");
  // Then we can actually parse it into a document
  const doc = parser.parseFromString(unescaped.documentElement.textContent, "text/html");

  const tagElList = doc.querySelectorAll("[data-tag]");
  for (const tagEl of tagElList) {
    const tag = findReplacementNameByTag(tagEl.dataset.tag);
    if (tag !== undefined) {
      tagEl.textContent = tag.name;
      if (window?._debug) {
        tagEl.title = tag.tag;
      }
    }
  }

  const tagsUnlessList = doc.querySelectorAll(".ifntag");
  for (const tagsUnless of tagsUnlessList) {
    const tags = tagsUnless.querySelectorAll(".iftag-label");
    if (tags[0] !== undefined && tags[1] !== undefined) {
      tags[0].textContent = CKEDITOR_TAGS_NAME.unless;
      tags[1].textContent = CKEDITOR_TAGS_NAME.then;
    }
  }

  const tagsIfList = doc.querySelectorAll(".iftag");
  for (const tagsIf of tagsIfList) {
    const tags = tagsIf.querySelectorAll(".iftag-label");
    if (tags[0] !== undefined && tags[1] !== undefined) {
      tags[0].textContent = CKEDITOR_TAGS_NAME.if;
      tags[1].textContent = CKEDITOR_TAGS_NAME.then;
    }
  }

  return doc.body.innerHTML;
}

// This variable is reset by initTagsList (needs to stay a var due to hoisting)
// TODO(Antoine): it's just a cache, is it really useful?
var replacementTagToNameLookup = undefined;

/**
 * @param {String} tag Tag of the replacement tag
 * @returns The name of the replacement tag which tag corresponds to the one passed in argument
 */
function findReplacementNameByTag(tag) {
  // build lookup table the first time we pass here
  if (replacementTagToNameLookup === undefined) {
    replacementTagToNameLookup = {};
    for (const tag of window.replacements_tags) {
      replacementTagToNameLookup[tag.tag] = tag;
    }
  }
  return replacementTagToNameLookup[tag];
}

// Draggable badges that insert a block widget rather than a replacement tag.
// Each id doubles as the data transfer key its plugin listens for on paste.
const BLOCK_WIDGET_IDS = ["iftag", "ifntag", "thirds"];

//--------------------------------------------------------------------------------------------------------------
// Tag plugin
//--------------------------------------------------------------------------------------------------------------
function createTagPlugin() {
  CKEDITOR.plugins.add("tag", {
    requires: "widget",

    init: function (editor) {
      editor.widgets.add("tag", {
        allowedContent: "span(!tag)[!data-tag,title]; div(!tag)[!data-tag,title]",
        requiredContent: "span(!tag)[!data-tag]; div(!tag)[!data-tag]",
        pathName: "tag",

        upcast: function (el) {
          return (el.name === "span" || el.name === "div") && el.hasClass("tag");
        },

        downcast: function (el) {
          // Remove the tag-error class before downcasting, this allows to not
          // store tag-error in the database, and avoid transfer it on paste
          if (el.attributes["class"]) {
            el.attributes["class"] = el.attributes["class"].replace("tag-error", "");
          }
          // Delete title, it's a debug attribute that should not be saved
          if (el.attributes["title"]) {
            el.attributes["title"] = "";
          }
          return el;
        },
      });

      // Register manually because there is no button
      editor.addFeature(editor.widgets.registered.tag);

      editor.on("drop", function (evt) {
        handleGeneratedFileSignDrop(editor, evt);
      });

      editor.on("paste", function (evt) {
        handleGeneratedFileSignDrop(editor, evt);

        const tag = evt.data.dataTransfer.getData("tag");

        if (!tag) {
          return;
        }

        // on Drop, transform to HTML
        if (/table|_form/i.test(tag.name)) {
          evt.data.dataValue = `<div class="tag" data-tag="${tag.tag}" title="${tag.tag}">${tag.name}</div>`;
        } else {
          evt.data.dataValue = `<span class="tag" data-tag="${tag.tag}" title="${tag.tag}">${tag.name}</span>`;
        }
      });

      editor.widgets.on("instanceDestroyed", function (event) {
        if (event.data.name !== "tag") {
          return;
        }

        const $el = event.data.element.$;
        removeErroredTag($el.textContent);
      });

      editor.widgets.on("instanceCreated", function (event) {
        if (event.data.name !== "tag") {
          return;
        }

        const $el = event.data.element.$;
        const tag = $el.getAttribute("data-tag");

        if (tag && !findReplacementNameByTag(tag)) {
          $el.classList.add("tag-error");
          addErroredTag($el.textContent);
        }
      });
    },
  });

  // When an item is dragged, copy its data into the drag and drop data transfer.
  // Delegated on the list itself: initTagsList replaces its children on every
  // filter keystroke, so a listener bound to a badge would die on first search.
  function onTagDragStart(evt) {
    var target = evt.data.getTarget().getAscendant("div", true);
    CKEDITOR.plugins.clipboard.initDragDataTransfer(evt);
    var dataTransfer = evt.data.dataTransfer;
    const widgetId = target.getId();

    if (BLOCK_WIDGET_IDS.includes(widgetId)) {
      dataTransfer.setData(widgetId, {});
    } else {
      const tag = window.replacements_tags[target.data("tag-index")];

      if (tag) {
        // Setting null or undefined value in datatransfer in ff results in text in receiver
        dataTransfer.setData("tag", tag);
      }
    }
    dataTransfer.setData("text/html", target.getText());
  }

  CKEDITOR.on("instanceReady", function () {
    for (const listId of [TAGS_LIST_ID, EMAIL_TAGS_LIST_ID]) {
      const list = CKEDITOR.document.getById(listId);

      if (list) {
        list.removeListener("dragstart", onTagDragStart);
        list.on("dragstart", onTagDragStart);
      }
    }

    // By default, CKEditor doesn't check for deleted widgets when the user
    // deletes content, since the the keyboard events (delete, cut, etc.) are
    // directly handled by the browser in the contenteditable element.
    //
    // This means the widget is still in CKEditor's internal state, but not in
    // the dom for a while, until it notices and cleans it up (firing the
    // built-in instanceDestroyed event). Think of it more like a garbage
    // collector.
    //
    // This does its best to detect when the user deletes content, and calls the
    // widget's checkWidgets method, which will destroy the widget if it's not
    // in the dom anymore.
    Object.keys(CKEDITOR.instances).forEach((key) => {
      CKEDITOR.instances[key].document.$.addEventListener("keyup", function (e) {
        if (
          (e.key === "x" && e.ctrlKey ^ e.metaKey) ||
          (e.key === "z" && e.ctrlKey ^ e.metaKey) ||
          e.key === "Backspace" ||
          e.key === "Delete"
        ) {
          CKEDITOR.instances[key].widgets.checkWidgets();
        }
      });
    });
  });
}

//--------------------------------------------------------------------------------------------------------------
// IfTag plugin
//--------------------------------------------------------------------------------------------------------------
function createIfTagPlugin(iftag, ifText) {
  CKEDITOR.plugins.add(iftag, {
    requires: "widget",

    init: function (editor) {
      editor.widgets.add(iftag, {
        allowedContent: `div(!${iftag}); div(!iftag-condition); div(!iftag-body); span(!iftag-label)`,
        requiredContent: `div(!${iftag}); div(!iftag-condition); div(!iftag-body); span(!iftag-label)`,
        pathName: iftag,
        editables: {
          condition: {
            selector: ".iftag-condition",
            allowedContent: "span(!tag)[!data-tag]",
          },
          body: {
            selector: ".iftag-body",
          },
        },

        upcast: function (el) {
          return el.name == "div" && el.hasClass(iftag);
        },
      });

      // Register manually because there is no button
      editor.addFeature(editor.widgets.registered[iftag]);

      // on Drop, transform to HTML
      editor.on("paste", function (evt) {
        var iftagData = evt.data.dataTransfer.getData(iftag);
        if (!iftagData) {
          return;
        }

        evt.data.dataValue = `
        <div class="${iftag}">
          <span class="iftag-label">${ifText}</span>
          <div class="iftag-condition"></div>
          <span class="iftag-label">${CKEDITOR_TAGS_NAME.then}</span>
          <div class="iftag-body"></div>
        </div>`;
      });
    },
  });
}

// pages have padding on top and on the bottom, we take that into account in the band heights.
const THIRDS_BANDS = [321, 373, 310];

const thirdsRow = (height) =>
  `<tr class="thirds-row" height="${height}"><td class="thirds-section" valign="top"><p><br></p></td></tr>`;

// One table rather than one per page: the page break is a CSS rule on the fourth row, so nothing can
// end up between the two halves and push the verso out of line with the recto.
const THIRDS_PAGE_HTML = `
  <table class="thirds-page" width="100%" cellpadding="0" cellspacing="0">
    ${[...THIRDS_BANDS, ...THIRDS_BANDS].map(thirdsRow).join("")}
  </table>`;

function createThirdsPlugin() {
  CKEDITOR.plugins.add("thirds", {
    init: function (editor) {
      editor.filter.allow(
        "table(thirds-page)[width,cellpadding,cellspacing]; tr(thirds-row)[height]; td(thirds-section)[valign]",
        "thirds",
        true
      );

      editor.on("paste", function (evt) {
        if (!evt.data.dataTransfer.getData("thirds")) {
          return;
        }

        if (editor.name !== CONTENT_EDITOR_NAME) {
          evt.stop();
          Toast.show("#thirds-wrong-editor-toast");
          return;
        }

        // Only a real table counts, so prose that merely mentions the class name doesn't lock the editor.
        if (editor.document?.$?.querySelector("table.thirds-page")) {
          evt.stop();
          Toast.show("#thirds-already-present-toast");
          return;
        }

        // Nested in a cell, the row heights and the page break would lay out the inner table, not the sheet.
        if (hasTableAncestor(getDropTarget(editor, evt))) {
          evt.stop();
          Toast.show("#thirds-table-error-toast");
          return;
        }

        evt.data.dataValue = THIRDS_PAGE_HTML;
      });
    },
  });
}

function hasTableAncestor(node) {
  while (node?.parentNode) {
    if (node?.localName === "table") {
      return true;
    }
    node = node?.parentNode;
  }
  return false;
}

function findTagInHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tagEl = doc.querySelector("[data-tag]");
  if (tagEl) {
    return tagEl.getAttribute("data-tag");
  }
  return null;
}

function dataIsSignature(data) {
  return (
    data?.dataTransfer?.getData("tag")?.tag === "generated_file_sign" ||
    findTagInHtml(data.dataTransfer.getData("text/html")) === "generated_file_sign"
  );
}

/**
 * Prevents signature tags from being dropped or pasted inside tables.
 * Signature tags have special positioning requirements and cannot be properly rendered
 * within table cells, so this handler blocks the drop/paste event when the target location
 * is inside a table structure.
 */
function handleGeneratedFileSignDrop(editor, evt) {
  if (!dataIsSignature(evt.data)) {
    return;
  }

  // signature can only be placed in document body

  if (editor.name !== CONTENT_EDITOR_NAME) {
    evt.stop();
    Toast.show("#signature-tag-wrong-editor-toast");
    return;
  }

  // check if another signature tag is already present

  const existingSignatureTag = editor.document.$.querySelector("[data-tag=generated_file_sign]");
  if (existingSignatureTag) {
    evt.stop();
    Toast.show("#signature-tag-already-present-toast");
    return;
  }

  // only one tag => check that it can be placed

  const maybeTarget = getDropTarget(editor, evt);
  if (maybeTarget && hasTableAncestor(maybeTarget)) {
    evt.stop();
    Toast.show("#signature-tag-table-error-toast");
  }
}

function getDropTarget(editor, evt) {
  if (evt?.name === "drop") {
    return evt?.data?.target?.$;
  }

  // An external drop fires "paste" with no range: CKEditor selects the drop position before firing,
  // so the selection is the drop target. Only an internal drag (within the same editor) carries a range.
  return evt.data?.range?.startContainer?.$ || editor.window.$.getSelection().anchorNode;
}

//--------------------------------------------------------------------------------------------------------------
// "One line" plugin - prevents newlines in single-line editors
//--------------------------------------------------------------------------------------------------------------
function createOneLinePlugin() {
  CKEDITOR.plugins.add("oneLine", {
    init: function (editor) {
      // Prevent Enter key from creating new lines
      editor.on("key", function (e) {
        if (e.data.keyCode === 13 /*enter*/ || e.data.keyCode === 2228237 /*shift + enter*/) {
          e.cancel();
        }
      });

      // Filter pasted content so only <span> elements (and text) remain.
      editor.on("paste", function (e) {
        // Remove <p> tags and <div> tags
        e.data.dataValue = e.data.dataValue
          .replace(/<p>/g, "")
          .replace(/<\/p>/g, "")
          .replace(/<div>/g, "")
          .replace(/<\/div>/g, "")
          .replace(/<br[^>]*>/g, "");
      });
    },
  });
}
